import { Box, IconButton, Stack, Typography } from '@mui/material';
import React, { useRef, useState } from 'react';
import Iconify from 'src/components/iconify/Iconify';
import { openVisitAttachment } from 'src/utils/openVisitAttachment';
import { getFileIcon } from '../../profile/components/ProfileFilesTable';

interface CarouselDocument {
  filename: string;
  originalFilename?: string;
  uploadedAt?: string;
  createdAt?: string;
}

interface CarouselProps {
  documents: CarouselDocument[];
  /** Visit the attachments belong to — used to re-sign each file on open. */
  visitId: string;
  /** Public guest surface → re-sign via the guest endpoint instead of the authed one. */
  guest?: boolean;
  /** Guest session id (X-Guest-Session) — required when `guest` is true. */
  sessionId?: string;
}

const DocumentCarousel: React.FC<CarouselProps> = ({
  documents,
  visitId,
  guest = false,
  sessionId,
}) => {
  const elementRef = useRef(null);
  const [arrowDisable, setArrowDisable] = useState(true);

  // Sort documents by date (newest first)
  const sortedDocuments = [...documents].sort((a, b) => {
    const dateA = new Date(a.uploadedAt || a.createdAt || 0).getTime();
    const dateB = new Date(b.uploadedAt || b.createdAt || 0).getTime();
    return dateB - dateA;
  });

  const handleHorizantalScroll = (
    element: any,
    speed: any,
    distance: any,
    step: any,
  ) => {
    let scrollAmount = 0;
    const slideTimer = setInterval(() => {
      element.scrollLeft += step;
      scrollAmount += Math.abs(step);
      if (scrollAmount >= distance) {
        clearInterval(slideTimer);
      }
      if (element.scrollLeft === 0) {
        setArrowDisable(true);
      } else {
        setArrowDisable(false);
      }
    }, speed);
  };

  if (!documents.length) {
    return null;
  }

  return (
    <Box
      sx={{
        px: 5,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <IconButton
        sx={{ position: 'absolute', left: 0 }}
        disabled={arrowDisable}
        onClick={() => {
          handleHorizantalScroll(elementRef.current, 5, 250, -20);
        }}
      >
        <Iconify icon="ic:round-chevron-left" width={20} />
      </IconButton>
      <IconButton
        sx={{ position: 'absolute', right: 0 }}
        onClick={() => {
          handleHorizantalScroll(elementRef.current, 5, 250, 20);
        }}
      >
        <Iconify icon="ic:round-chevron-right" width={20} />
      </IconButton>
      <Box
        sx={{
          display: 'flex',
          overflow: 'hidden',
          width: '100%',
          gap: 3,
        }}
        ref={elementRef}
      >
        {sortedDocuments.map((file, index) => (
          <Stack
            key={index}
            sx={{
              gap: 2,
              flexDirection: 'row',
              alignItems: 'center',
              width: 'fit-content',
            }}
          >
            <Box
              sx={{
                padding: 1,
                borderRadius: 1,
                backgroundColor: 'background.neutral', // Adjust as per theme
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {getFileIcon(file.originalFilename || file.filename)}
            </Box>
            <Typography
              sx={{
                whiteSpace: 'nowrap',
                '&:hover': {
                  cursor: 'pointer',
                  textDecoration: 'underline',
                },
              }}
              variant="body2"
              onClick={async () => {
                try {
                  await openVisitAttachment({
                    visitId,
                    filename: file.filename,
                    guest,
                    sessionId,
                  });
                } catch (e) {
                  console.error('Failed to open attachment', e);
                }
              }}
            >
              {file.originalFilename || file.filename.split('_')[1] || file.filename}
            </Typography>
          </Stack>
        ))}
      </Box>
    </Box>
  );
};

export default DocumentCarousel;
